import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { AlertIcon, RenameIcon } from './Icons';
import { holdModal } from '../lib/modal';
import { useT } from '../lib/i18n';

/**
 * Asking before doing something that cannot be undone.
 *
 * This replaces `window.confirm` and `window.prompt`, which the studio used
 * for deleting a garment, throwing away a roll, discarding an edited model and
 * naming a house style. Those dialogs are the browser's, not the studio's:
 * they arrive in the system font at the top of the screen, they cannot say
 * which button is the dangerous one, and on a phone they read as though the
 * page has done something wrong. They also block the main thread, so nothing
 * animates while one is open.
 *
 * One promise-shaped API, so calling it stays as easy as the thing it replaces:
 *
 *   if (!(await ask({ title: …, danger: true }))) return;
 *   const name = await prompt({ title: …, value: … });
 */
interface AskOptions {
  title: string;
  body?: string;
  /** Defaults to a plain "OK"; a destructive action should name what it does. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirming button as destructive and focuses cancel instead. */
  danger?: boolean;
}

interface PromptOptions extends AskOptions {
  value?: string;
  placeholder?: string;
  label?: string;
}

interface DialogApi {
  ask: (options: AskOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | undefined>;
}

const DialogContext = createContext<DialogApi>({
  ask: async () => false,
  prompt: async () => undefined,
});

export const useDialog = () => useContext(DialogContext);

type Pending =
  | { kind: 'ask'; options: AskOptions; settle: (value: boolean) => void }
  | { kind: 'prompt'; options: PromptOptions; settle: (value: string | undefined) => void };

export function DialogProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [pending, setPending] = useState<Pending | undefined>();
  const [draft, setDraft] = useState('');
  const field = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const ask = useCallback(
    (options: AskOptions) =>
      new Promise<boolean>((resolve) => setPending({ kind: 'ask', options, settle: resolve })),
    [],
  );

  const prompt = useCallback(
    (options: PromptOptions) =>
      new Promise<string | undefined>((resolve) => {
        setDraft(options.value ?? '');
        setPending({ kind: 'prompt', options, settle: resolve });
      }),
    [],
  );

  // While a question is up, the page is covered and the shell knows it; and
  // when it is answered, focus goes back to whatever asked — the field in a
  // sheet, say — rather than falling to the body. Declared before the effect
  // that moves focus into the dialog, so it remembers the right element.
  useEffect(() => {
    if (!pending) return;
    const release = holdModal();
    const previouslyFocused = document.activeElement as HTMLElement | null;
    return () => {
      release();
      previouslyFocused?.focus?.();
    };
  }, [pending]);

  /* Focus lands inside the dialog — on the field if there is one, and on
     cancel rather than confirm when the action is destructive. */
  useEffect(() => {
    if (!pending) return;
    const target =
      pending.kind === 'prompt'
        ? field.current
        : pending.options.danger
          ? cancelRef.current
          : confirmRef.current;
    target?.focus();
    if (pending.kind === 'prompt') field.current?.select();
  }, [pending]);

  const close = (value: boolean | string | undefined) => {
    if (!pending) return;
    if (pending.kind === 'ask') pending.settle(Boolean(value));
    else pending.settle(value === false ? undefined : (value as string | undefined));
    setPending(undefined);
  };

  // Escape always means no, from anywhere in the dialog.
  useEffect(() => {
    if (!pending) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(pending.kind === 'ask' ? false : undefined);
      }
    };
    /*
     * Bound a task later, not now. A dialog opened *by* a key press is
     * mounted while that press is still bubbling, and a listener added here
     * would catch it on its way to the window and answer the question with
     * the same key that asked it. Nothing in flight can reach a listener
     * added in a later task.
     */
    const timer = window.setTimeout(() => window.addEventListener('keydown', onKey), 0);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  });

  const options = pending?.options;

  return (
    <DialogContext.Provider value={{ ask, prompt }}>
      {children}

      {pending && options && (
        <div
          className="dialog__scrim"
          role="presentation"
          onClick={() => close(pending.kind === 'ask' ? false : undefined)}
        >
          <div
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            {/*
             * A face before the words. A question that cannot be taken back
             * and a question about a name arrived as the same white box with
             * the same two buttons, and the only thing telling them apart was
             * a sentence you had to read first. The tile is read before the
             * sentence is.
             */}
            <span className={`dialog__mark ${options.danger ? 'is-danger' : ''}`} aria-hidden="true">
              {options.danger ? <AlertIcon /> : <RenameIcon />}
            </span>

            <h2 className="dialog__title" id="dialog-title">
              {options.title}
            </h2>
            {options.body && <p className="dialog__body">{options.body}</p>}

            {pending.kind === 'prompt' && (
              <div className="field">
                {pending.options.label && <label htmlFor="dialog-field">{pending.options.label}</label>}
                <input
                  id="dialog-field"
                  ref={field}
                  className="input"
                  value={draft}
                  placeholder={pending.options.placeholder}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && draft.trim()) close(draft);
                  }}
                />
              </div>
            )}

            <div className="dialog__feet">
              <button
                type="button"
                ref={cancelRef}
                className="btn btn--ghost"
                onClick={() => close(pending.kind === 'ask' ? false : undefined)}
              >
                {options.cancelLabel ?? t('common.cancel')}
              </button>
              <button
                type="button"
                ref={confirmRef}
                className={`btn ${options.danger ? 'btn--danger btn--solid' : 'btn--primary'}`}
                disabled={pending.kind === 'prompt' && !draft.trim()}
                onClick={() => close(pending.kind === 'prompt' ? draft : true)}
              >
                {options.confirmLabel ?? t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}
